import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Flame } from "lucide-react";
import { resolveImage } from "@/lib/api";

const CAT_LABELS = {
  "mattresses": "Mattresses",
  "bedroom-set": "Bedroom Sets",
  "dining-room": "Dining Room",
  "living-room": "Living Room",
  "recliners": "Recliners",
};
const CAT_ORDER = ["bedroom-set", "dining-room", "living-room", "recliners", "mattresses"];

export const ProductsSection = ({ products, phone, activeCat, setActiveCat }) => {
  const cats = useMemo(() => {
    const present = new Set(products.map((p) => p.category));
    return CAT_ORDER.filter((c) => present.has(c));
  }, [products]);

  const current = activeCat && cats.includes(activeCat) ? activeCat : cats[0];
  const items = products.filter((p) => p.category === current);

  if (!products.length) return null;

  return (
    <section id="sale" className="relative py-24 sm:py-32 px-5 sm:px-8 bg-ad-black">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          <p className="text-ad-red text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-ad-red" /> Hot Buy Sale — Prices Effective May–Aug 2026
          </p>
          <h2 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] tracking-tight">
            Shop The <span className="metallic-text">Sale</span>
          </h2>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-10 border-b border-white/10 pb-5">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`sale-tab-${c}`}
              onClick={() => setActiveCat(c)}
              className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors ${
                current === c ? "bg-ad-red text-white" : "border border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {CAT_LABELS[c] || c}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {items.map((p, i) => (
              <ProductCard key={p.id} product={p} phone={phone} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>

        {!items.length && (
          <p className="text-white/40 text-center py-16">New arrivals coming soon. Call us for current inventory.</p>
        )}
      </div>
    </section>
  );
};

const ProductCard = ({ product, phone, index }) => {
  const tel = (phone || "").replace(/[^0-9]/g, "");
  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.05 }}
      className="group relative bg-white border border-white/10 overflow-hidden"
    >
      {product.featured && (
        <span
          data-testid="featured-badge"
          className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-ad-red text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1"
        >
          <Flame size={12} /> Hot Buy
        </span>
      )}
      <div className="aspect-[4/5] w-full bg-neutral-100 overflow-hidden">
        <img
          src={resolveImage(product.image_url)}
          alt={product.title}
          loading="lazy"
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <a
        href={`tel:${tel}`}
        className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-ad-red text-white flex items-center justify-center gap-2 py-3 text-sm font-bold uppercase tracking-widest"
      >
        <Phone size={15} /> Call To Order
      </a>
    </motion.div>
  );
};
